import { CloseButton, Input, NativeSelect, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Plus } from "lucide-react";
import type { ReactNode } from "react";

import { FieldLabel } from "../../ui/FieldLabel";
import type { ColorDraft } from "./drafts";
import classes from "./forms.module.css";

/** The live forms' color names; the API takes any label, so another one shows as itself. */
const LABELS = ["Primary", "Secondary", "Accent", "Highlight", "Background"];

/** A color the user adds, before they pick it: the live form's gray, "Primary" first. */
function newColor(count: number): ColorDraft {
  return { hex: "#888888", label: count === 0 ? "Primary" : "Accent" }; // raw-color: data
}

function labelOptions(current: string) {
  const options = LABELS.map((label) => ({ value: label, label }));
  if (current.trim() === "") return [{ value: "", label: "No label" }, ...options];
  if (!LABELS.includes(current)) return [{ value: current, label: current }, ...options];
  return options;
}

export interface ColorsInputProps {
  value: ColorDraft[];
  onChange?: (colors: ColorDraft[]) => void;
  /** The schema's limit, such as `brandInColorsMax`. */
  max: number;
  label: string;
  description: string;
  info?: string;
  error?: string | undefined;
  /** A tile after the colors and the "+" tile: the product palette's Re-extract (G15c). */
  trailing?: ReactNode;
}

/**
 * Up to `max` colors as the live form's tiles (A3 `Color`, design-system.md 3.6): an 83px swatch
 * that opens the browser's color picker, its label under it, a remove button on hover, and a
 * dashed "+" tile while there is room. Without `onChange` the tiles only show the colors.
 */
export function ColorsInput(props: ColorsInputProps) {
  const { value, onChange, max, label, description, info, error, trailing } = props;
  const update = (index: number, patch: Partial<ColorDraft>) =>
    onChange?.(value.map((color, at) => (at === index ? { ...color, ...patch } : color)));
  return (
    <Input.Wrapper
      label={<FieldLabel label={label} {...(info === undefined ? {} : { info })} spoken />}
      description={description}
      error={error}
      // The tiles carry their own names; the wrapper's label names the group only.
      labelElement="div"
    >
      <div className={classes.colors}>
        {value.map((color, index) => (
          // Rows have no id of their own; each is the color at that position.
          <div key={index} className={classes.color}>
            <Tooltip label={color.hex} withArrow position="top" offset={11}>
              <div className={classes.swatch} style={{ backgroundColor: color.hex }}>
                {onChange === undefined ? null : (
                  <>
                    <input
                      type="color"
                      className={classes.swatchInput}
                      aria-label={`Color ${index + 1}`}
                      value={color.hex.toLowerCase()}
                      onChange={(event) =>
                        update(index, { hex: event.currentTarget.value.toUpperCase() })
                      }
                    />
                    <CloseButton
                      size="sm"
                      className={classes.remove}
                      aria-label={`Remove color ${index + 1}`}
                      onClick={() => onChange(value.filter((_, at) => at !== index))}
                    />
                  </>
                )}
              </div>
            </Tooltip>
            {onChange === undefined ? (
              <Text size="sm" ta="center" truncate c="var(--cai-color-text-muted)">
                {color.label}
              </Text>
            ) : (
              <NativeSelect
                size="xs"
                variant="unstyled"
                className={classes.colorLabel}
                aria-label={`Label ${index + 1}`}
                // Live shows the label alone, centered, with no chevron.
                rightSection={<span />}
                rightSectionWidth={0}
                data={labelOptions(color.label)}
                value={color.label}
                onChange={(event) => update(index, { label: event.currentTarget.value })}
              />
            )}
          </div>
        ))}
        {onChange !== undefined && value.length < max ? (
          <UnstyledButton
            className={`${classes.zoneAdd} ${classes.colorAdd}`}
            aria-label="Add a color"
            onClick={() => onChange([...value, newColor(value.length)])}
          >
            <Plus size={20} strokeWidth={1.5} aria-hidden />
          </UnstyledButton>
        ) : null}
        {trailing}
      </div>
    </Input.Wrapper>
  );
}
